import React, { useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import { Cell, HeaderCell, HeaderRow, Row, SortLabel, Table, TableBody, TableHead } from 'components';
import { ICollectedTipsData, IPaymentSetCalculations } from 'types';
import { centsToDollarsDisplay, formatCollectedTipsData, toTitleCase } from 'utils';
import { CSVFormatter } from 'utils/csvformatter';
import { IExportData } from './types';

const TableWrapper = styled.section`
  width: 100%;
`;

interface ICollectedTipsProps {
  header: (data?: IExportData) => React.ReactNode;
  calculations?: IPaymentSetCalculations;
  isLoading?: boolean;
}

const headers: { label: string; value: keyof ICollectedTipsData }[] = [
  { label: 'Payee', value: 'payee' },
  { label: 'Day Segment', value: 'daySegment' },
  { label: 'Job', value: 'jobTitle' },
  { label: 'Cash Tips', value: 'cashTips' },
  { label: 'Cash Autograts', value: 'cashAutograts' },
  { label: 'CC Tips', value: 'ccTips' },
  { label: 'CC Autograts', value: 'ccAutograts' },
  { label: 'CC Fee', value: 'ccFee' },
  { label: 'Net', value: 'net' },
];

const CollectedTips = ({ header, calculations, isLoading }: ICollectedTipsProps) => {
  const [sortBy, setSortBy] = useState<{ field: keyof ICollectedTipsData; direction: 'asc' | 'desc' }>({ field: 'daySegment', direction: 'asc' });

  const data = formatCollectedTipsData(calculations, sortBy);
  const exportCollectedTipsCSV = async () => CSVFormatter.collectedTipsPayout(data);

  return (
    <>
      {header({ exportFunc: exportCollectedTipsCSV, fileName: 'CollectedTips' })}
      <TableWrapper data-cy="table">
        <Table scrollable sticky data-cy="collected-tips-table">
          <TableHead>
            <HeaderRow sticky>
              {headers.map(({ label, value }) => (
                <HeaderCell key={`${value}-header`}>
                  <SortLabel
                    label={label}
                    data-cy={`${value}-label`}
                    isSorted={sortBy.field === value}
                    isSortedDesc={sortBy.direction === 'asc'}
                    isLoading={isLoading}
                    onClick={() => setSortBy({ field: value, direction: sortBy.direction === 'asc' ? 'desc' : 'asc' })}
                  />
                </HeaderCell>
              ))}
            </HeaderRow>
          </TableHead>
          <TableBody>
            {data.map((row: ICollectedTipsData) => (
              <Row key={`collected-${row.id}`}>
                <Cell wrapText minWidth={150} data-label="Payee" fontWeight="semiBold">
                  {isLoading ? <Skeleton width={50} /> : row.payee || '--'}
                </Cell>
                <Cell wrapText minWidth={150} data-label="DaySegment">
                  {isLoading ? <Skeleton width={50} /> : row.daySegment || '--'}
                </Cell>
                <Cell data-label="JobTitle">{isLoading ? <Skeleton width={50} /> : toTitleCase(row.jobTitle || '--')}</Cell>
                <Cell data-label="CashTips">{isLoading ? <Skeleton width={50} /> : centsToDollarsDisplay(row.cashTips)}</Cell>
                <Cell data-label="CashAutograts">{isLoading ? <Skeleton width={50} /> : centsToDollarsDisplay(row.cashAutograts)}</Cell>
                <Cell data-label="CCTips">{isLoading ? <Skeleton width={50} /> : centsToDollarsDisplay(row.ccTips)}</Cell>
                <Cell data-label="CCAutograts">{isLoading ? <Skeleton width={50} /> : centsToDollarsDisplay(row.ccAutograts)}</Cell>
                <Cell data-label="CCFee">{isLoading ? <Skeleton width={50} /> : centsToDollarsDisplay(row.ccFee)}</Cell>
                <Cell data-label="Net" fontWeight="semiBold">
                  {isLoading ? <Skeleton width={50} /> : centsToDollarsDisplay(row.net)}
                </Cell>
              </Row>
            ))}
          </TableBody>
        </Table>
      </TableWrapper>
    </>
  );
};

export default CollectedTips;
